<template>
  <div
    class="slm"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.SpeakersListModal.allSpeakers')"
  >
    <!-- Header -->
    <div class="slm__header">
      <div class="slm__title-block">
        <h2 class="slm__title">
          {{ $t('Components.SpeakersListModal.speakers') }}
        </h2>
        <p class="slm__sub">{{ totalLabel }}</p>
      </div>
      <button
        class="slm__close"
        @click="onClose"
        :aria-label="$t('Components.SpeakersListModal.close')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Search + category filter — sit side by side on desktop -->
    <div class="slm__filters">
      <div class="slm__search-wrap">
        <i class="fas fa-search slm__search-icon" aria-hidden="true"></i>
        <input
          v-model="searchInput"
          type="text"
          class="slm__search"
          :placeholder="$t('Components.SpeakersListModal.searchPlaceholder')"
          :aria-label="$t('Components.SpeakersListModal.searchSpeakers')"
        />
      </div>

      <div v-if="speakerCategoryOptions.length" class="slm__category-wrap">
        <select
          v-model="selectedCategoryId"
          class="slm__category"
          :aria-label="$t('Components.SpeakersListModal.filterByCategory')"
        >
          <option :value="null">
            {{ $t('Components.SpeakersListModal.allCategories') }}
          </option>
          <option
            v-for="cat in speakerCategoryOptions"
            :key="cat.value"
            :value="cat.value"
          >
            {{ cat.label }}
          </option>
        </select>
        <i
          class="fas fa-chevron-down slm__category-chevron"
          aria-hidden="true"
        ></i>
      </div>
    </div>

    <!-- Vertical list -->
    <div class="slm__list">
      <div
        v-for="speaker in filteredSpeakers"
        :key="speaker.id || speaker.UserID"
        class="slm-row"
        role="button"
        tabindex="0"
        @click="openSpeaker(speaker)"
        @keypress.enter="openSpeaker(speaker)"
      >
        <div class="slm-row__avatar-wrap">
          <Avatar
            :image="speaker.profileImageUrl"
            :circle="true"
            :name="fullName(speaker)"
            :alt="fullName(speaker)"
            class="slm-row__avatar"
          />
        </div>

        <div class="slm-row__info">
          <div class="slm-row__name">{{ fullName(speaker) }}</div>
          <div v-if="speaker.jobTitle" class="slm-row__title">
            {{ speaker.jobTitle }}
          </div>
          <div v-if="speaker.Company" class="slm-row__company">
            {{ speaker.Company }}
          </div>
        </div>

        <i class="fas fa-chevron-right slm-row__chevron" aria-hidden="true"></i>
      </div>

      <div v-if="!filteredSpeakers.length" class="slm-empty">
        <i
          class="far fa-user"
          style="font-size: 30px; color: #d1d5db; margin-bottom: 8px"
        ></i>
        <p>{{ $t('Components.SpeakersListModal.noResults') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Avatar from '@/components/shared/Avatar';
import SpeakerInfoModal from '@/components/SpeakerInfoModal';
import focusTrapMixin from '@/mixins/focusTrap';

export default {
  name: 'SpeakersListModal',
  components: { Avatar },
  mixins: [focusTrapMixin],
  data() {
    return {
      searchInput: '',
      selectedCategoryId: null
    };
  },
  computed: {
    ...mapGetters('Speakers', { allSpeakers: 'getItems' }),
    ...mapGetters('Categories', {
      speakersCategories: 'getSpeakersCategories',
      allCategoriesRaw: 'getItems'
    }),
    allCategories() {
      const raw = this.allCategoriesRaw;
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === 'object') return Object.values(raw);
      return [];
    },
    activeSpeakers() {
      if (!this.allSpeakers) return [];
      const list = Array.isArray(this.allSpeakers)
        ? this.allSpeakers
        : Object.values(this.allSpeakers);
      return list.filter((s) => s && s.active !== false);
    },
    // Lookup from category id (any case) to its normalized label
    // key. Built from BOTH the typed `speakersCategories` getter
    // and any other categories the entities reference, so a
    // speaker tagged with category id N still finds a label even
    // if the typed getter's filter (`categoryType === SPEAKER`)
    // missed it.
    categoryIdToKey() {
      const map = new Map();
      const seedFrom = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((c) => {
          if (!c) return;
          const id = this.pickCategoryId(c);
          const label = this.pickCategoryLabel(c);
          if (id && label) {
            map.set(String(id), String(label).trim().toLowerCase());
          }
        });
      };
      // Seed from ALL categories in the store, not only those whose
      // categoryType matches SPEAKER — admin-defined categories may
      // carry a different (or missing) type, but the speaker still
      // references them by id.
      seedFrom(this.allCategories);
      seedFrom(this.speakersCategories);
      return map;
    },
    // Build the dropdown list. Each option is { value, label } where
    // `value` is the category's server value — the speaker list
    // endpoint doesn't return per-speaker categories, so filtering is
    // done server-side (see the `selectedCategoryId` watcher) and the
    // backend `category` query param matches on this value.
    speakerCategoryOptions() {
      const seen = new Map();
      const source = Array.isArray(this.speakersCategories)
        ? this.speakersCategories
        : [];
      source.forEach((c) => {
        if (!c) return;
        const value = this.speakerCategoryValue(c);
        if (value === '') return;
        const label = this.pickCategoryLabel(c) || value;
        // Dedup by the category's unique identity (id), not by `value`: two
        // distinct speaker categories can share or lack a `value`, and keying
        // on `value` collapsed them into a single dropdown option — the
        // "only one of two categories shows" bug. The option keeps the server
        // `value` so backend category filtering still matches.
        const key = this.pickCategoryId(c) || String(value);
        if (!seen.has(key)) {
          seen.set(key, { value: String(value), label });
        }
      });
      return Array.from(seen.values());
    },
    filteredSpeakers() {
      // Category filtering happens server-side; only the free-text
      // search is applied here.
      const q = (this.searchInput || '').trim().toLowerCase();
      if (!q) return this.activeSpeakers;
      return this.activeSpeakers.filter((s) => {
        const haystack = [this.fullName(s), s.jobTitle || '', s.Company || '']
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    },
    totalLabel() {
      const n = this.activeSpeakers.length;
      if (!n) return this.$t('Components.SpeakersListModal.noSpeakers');
      return this.$tc('Components.SpeakersListModal.speakerCount', n, { n });
    }
  },
  watch: {
    // Category filtering is server-side — refetch the speakers
    // whenever the selected category changes (null → full list).
    selectedCategoryId(val) {
      this.fetchSpeakers({ category: val || '' }).catch(() => {});
    }
  },
  created() {
    document.body.classList.add('overflow-hidden');
    // Prime the categories store so the filter dropdown has options,
    // and (re)load the full speaker list so the modal always opens
    // unfiltered even if a category filter was left active elsewhere.
    this.fetchCategories();
    this.fetchSpeakers().catch(() => {});
  },
  mounted() {
    this.activateFocusTrap();
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    ...mapActions('Categories', { fetchCategories: 'fetchItems' }),
    ...mapActions('Speakers', { fetchSpeakers: 'fetchSpeakers' }),
    // The value the backend `category` filter matches on — the
    // category's `value` field, falling back to its id.
    speakerCategoryValue(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      if (
        c.value !== undefined &&
        c.value !== null &&
        String(c.value).trim() !== ''
      ) {
        return String(c.value);
      }
      return this.pickCategoryId(c);
    },
    fullName(speaker) {
      if (!speaker) return '';
      const first = speaker.name || speaker.Firstname || '';
      const last = speaker.lastName || speaker.Lastname || '';
      return `${first} ${last}`.trim();
    },
    pickCategoryLabel(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'label',
        'Label',
        'value',
        'Value',
        'name',
        'Name',
        'title',
        'Title',
        'caption',
        'Caption',
        'text',
        'Text',
        'categoryName',
        'CategoryName',
        'displayName',
        'DisplayName'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickCategoryId(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'id',
        'Id',
        'ID',
        'categoryId',
        'CategoryId',
        'categoryID',
        'CategoryID',
        'cid',
        'Cid'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickEntityCategories(entity) {
      if (!entity) return [];
      const keys = [
        'speakerCategories',
        'SpeakerCategories',
        'sponsorCategories',
        'SponsorCategories',
        'speakerCategoryIds',
        'SpeakerCategoryIds',
        'sponsorCategoryIds',
        'SponsorCategoryIds',
        'categoryIds',
        'CategoryIds',
        'categories',
        'Categories',
        'category',
        'Category',
        'categoryId',
        'CategoryId'
      ];
      for (const k of keys) {
        const v = entity[k];
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) return v;
        return [v];
      }
      return [];
    },
    // Returns the set of normalized label keys this speaker is
    // tagged with. When the speaker's entry only carries an ID
    // (no inline label), resolve it against the categories store so
    // it still matches the dropdown's label-derived key.
    speakerCategoryKeys(speaker) {
      if (!speaker) return [];
      const raw = this.pickEntityCategories(speaker);
      if (!raw.length) return [];
      const lookup = this.categoryIdToKey;
      return raw
        .map((c) => {
          if (c === null || c === undefined) return '';
          const inlineLabel =
            typeof c === 'object' ? this.pickCategoryLabel(c) : '';
          if (inlineLabel && typeof c === 'object') {
            return String(inlineLabel).trim().toLowerCase();
          }
          const id = this.pickCategoryId(c);
          if (!id) {
            const scalar =
              typeof c === 'string' || typeof c === 'number' ? String(c) : '';
            return scalar ? scalar.trim().toLowerCase() : '';
          }
          const looked = lookup.get(String(id));
          return looked || String(id).trim().toLowerCase();
        })
        .filter(Boolean);
    },
    onClose() {
      this.$emit('close');
    },
    openSpeaker(speaker) {
      this.$modal.show(
        SpeakerInfoModal,
        { speaker },
        {
          width: '92%',
          height: 'auto',
          adaptive: true,
          minWidth: 300,
          maxWidth: 640
        },
        { closed: () => {} }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.slm {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 18px 16px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  &__title-block {
    flex: 1;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 2px;
  }

  &__sub {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  &__close {
    background: transparent;
    border: none;
    color: #6b7280;
    font-size: 18px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }
  }

  &__filters {
    display: flex;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 12px;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 8px;
    }
  }

  &__search-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 13px;
  }

  &__search {
    width: 100%;
    padding: 10px 14px 10px 36px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 14px;
    background: #f9fafb;
    outline: none;
    color: #111827;
    font-family: inherit;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: #d1d5db;
      background: #ffffff;
    }
  }

  &__category-wrap {
    position: relative;
    flex: 0 0 auto;
    min-width: 160px;

    @media (max-width: 480px) {
      min-width: 0;
    }
  }

  &__category {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    padding: 10px 36px 10px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 14px;
    font-weight: 500;
    background: #ffffff;
    color: #111827;
    cursor: pointer;
    outline: none;
    font-family: inherit;
    line-height: 1.3;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
      border-color: #d1d5db;
    }
    &:focus {
      border-color: #d1d5db;
    }
  }

  &__category-chevron {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    font-size: 11px;
  }

  &__list {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -6px;
    padding: 0 6px 4px;
  }
}

.slm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    outline: none;
  }

  & + & {
    border-top: 1px solid #f1f2f4;
  }

  &__avatar-wrap {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__avatar {
    width: 44px;
    height: 44px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title,
  &__company {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    color: #d1d5db;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.slm-empty {
  text-align: center;
  padding: 32px 12px;
  color: #6b7280;

  p {
    margin: 0;
    font-size: 13px;
  }
}
</style>
